import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, CalendarDays, X } from 'lucide-react';

var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function ProtoMonthPicker({ value, onChange }) {
  var now = new Date();
  var initYear = value && value !== 'all' ? parseInt(value.split('-')[0]) : now.getFullYear();

  var yearState = useState(initYear);
  var year = yearState[0];
  var setYear = yearState[1];

  var openState = useState(false);
  var open = openState[0];
  var setOpen = openState[1];

  var selectedMonth = value && value !== 'all' ? parseInt(value.split('-')[1]) - 1 : null;
  var selectedYear = value && value !== 'all' ? parseInt(value.split('-')[0]) : null;

  var displayLabel = 'All Months';
  if (value && value !== 'all') {
    displayLabel = MONTHS[selectedMonth] + ' ' + selectedYear;
  }

  function handleSelect(monthIdx) {
    var val = year + '-' + String(monthIdx + 1).padStart(2, '0');
    onChange(val);
    setOpen(false);
  }

  function handleClear() {
    onChange('all');
    setOpen(false);
  }

  function isFuture(monthIdx) {
    if (year > now.getFullYear()) return true;
    if (year === now.getFullYear() && monthIdx > now.getMonth()) return true;
    return false;
  }

  function isSelected(monthIdx) {
    return selectedYear === year && selectedMonth === monthIdx;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 text-sm font-normal min-w-[160px] justify-start"
          data-testid="proto-month-filter"
        >
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{displayLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-3" align="start" data-testid="month-picker-popover">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={function() { setYear(year - 1); }}
            className="p-1 rounded hover:bg-slate-100 transition-colors duration-100"
            data-testid="month-picker-prev-year"
          >
            <ChevronLeft className="h-4 w-4 text-slate-600" />
          </button>
          <span className="text-sm font-semibold">{year}</span>
          <button
            onClick={function() { if (year < now.getFullYear()) setYear(year + 1); }}
            className={'p-1 rounded transition-colors duration-100 ' + (year >= now.getFullYear() ? 'opacity-30 cursor-not-allowed' : 'hover:bg-slate-100')}
            data-testid="month-picker-next-year"
          >
            <ChevronRight className="h-4 w-4 text-slate-600" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          {MONTHS.map(function(m, idx) {
            var future = isFuture(idx);
            var active = isSelected(idx);
            var base = 'text-xs font-medium py-2 rounded-md transition-colors duration-100 ';
            var cls = base;
            if (future) {
              cls += 'text-slate-300 cursor-not-allowed';
            } else if (active) {
              cls += 'bg-slate-900 text-white';
            } else {
              cls += 'hover:bg-slate-100 text-slate-700';
            }
            return (
              <button
                key={m}
                disabled={future}
                onClick={function() { handleSelect(idx); }}
                className={cls}
                data-testid={'month-btn-' + m.toLowerCase()}
              >
                {m}
              </button>
            );
          })}
        </div>

        {value && value !== 'all' && (
          <button
            onClick={handleClear}
            className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-slate-700 py-1.5 rounded-md hover:bg-slate-50 transition-colors duration-100"
            data-testid="month-picker-clear"
          >
            <X className="h-3 w-3" /> Clear filter
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
